import { isUserRejection } from './userRejection'

// map error string to display message
const knownErrors: Record<string, string> = {
  'V2Router: EXPIRED': 'This transaction could not be sent because the deadline has passed.',
  'V2Router: INSUFFICIENT_OUTPUT_AMOUNT':
    'This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance',
  'V2Router: EXCESSIVE_INPUT_AMOUNT':
    'This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance',
  'TransferHelper: TRANSFER_FROM_FAILED':
    'The input token cannot be transferred. There may be an issue with the input token.',
  'V2: TRANSFER_FAILED': 'The output token cannot be transferred. There may be an issue with the output token.',
  'Too little received':
    'This transaction will not succeed due to price movement. Try increasing your slippage tolerance.',
  'Too much requested':
    'This transaction will not succeed due to price movement. Try increasing your slippage tolerance.',
  STF: 'This transaction will not succeed due to price movement. Try increasing your slippage tolerance.',
  TF: 'The output token cannot be transferred. There may be an issue with the output token.',
}

/**
 * Gets a user-friendly display message for known transaction errors
 * @param error The error object to check
 * @returns The display message if error matches a known pattern, undefined otherwise
 */
export function getErrorDisplayMessage(error: any): string | undefined {
  if (!error) return undefined

  // Get the error message
  const errorMessage = error.message?.toLowerCase() || error.toString().toLowerCase()

  // Check if error message contains any of the configured error strings
  for (const [errorKey, displayMessage] of Object.entries(knownErrors)) {
    if (errorMessage.includes(errorKey.toLowerCase())) {
      return displayMessage
    }
  }

  return undefined
}

/**
 * Recursively extracts the error reason from an error object
 * Checks multiple possible locations where error messages might be stored
 */
function extractErrorReason(error: any): string | undefined {
  if (!error) return undefined

  // Check common error message locations
  let reason = error.reason ?? error.message ?? error.data?.message

  // Recursively check nested error objects
  if (!reason && error.error) {
    reason = extractErrorReason(error.error)
  }

  // Check originalError in data
  if (!reason && error.data?.originalError) {
    reason = extractErrorReason(error.data.originalError)
  }

  return reason
}

/**
 * Main function to check and transform error messages into user-friendly text
 * @param error The error object from the transaction
 * @param fallbackMessage Optional fallback message if no error can be extracted
 * @returns A user-friendly error message
 */
export function checkErrorMessage(error?: any, fallbackMessage?: string): string {
  // If no error provided, return fallback
  if (!error) {
    return fallbackMessage || 'Transaction failed'
  }

  // If error is already a string, use it directly
  if (typeof error === 'string') {
    const displayMessage = getErrorDisplayMessage({ message: error })
    return displayMessage || fallbackMessage || 'Transaction failed'
  }

  // Check if user rejected the transaction
  if (isUserRejection(error)) {
    return 'Transaction rejected'
  }

  // Extract the error reason recursively
  const reason = extractErrorReason(error)

  // If we found a reason, try to get a user-friendly message
  if (reason) {
    // Check if we have a configured display message for this error
    const displayMessage = getErrorDisplayMessage(error)
    if (displayMessage) {
      return displayMessage
    }

    return reason
  }

  // If no reason found, return fallback
  return fallbackMessage || 'Transaction failed'
}
